import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Header } from '../../components/layout/Header';
import { API } from '../../utils/api';
import { trackDocumentUploadStarted, trackDocumentUploadCompleted } from '../../utils/analytics';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import DocumentReviewResult from '../../components/DocumentReviewResult';
import { 
  CloudArrowUp, 
  FileText, 
  CheckCircle, 
  Clock,
  Trash,
  MagnifyingGlass,
  Warning,
  Lock,
  Star,
  Sparkle,
  CaretDown,
  CaretUp,
  CircleNotch,
  PaperPlaneTilt,
  ArrowLeft,
  X
} from '@phosphor-icons/react';

export default function DocumentsPage() {
  const { language, t } = useLanguage();
  const { isAuthenticated, isPaid } = useAuth();
  const navigate = useNavigate();
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null); // For full-screen review view

  const translations = {
    en: {
      title: "Document Review",
      subtitle: "Upload your documents and get helpful feedback before submitting",
      dragDrop: "Drop your file here, or click to browse",
      allowedTypes: "PDF, JPG, PNG (max 10MB)",
      uploadLocked: "Upload locked",
      availableWithPremium: "Available with Premium",
      noDocuments: "No documents uploaded yet",
      uploadToStart: "Upload a document to get started",
      reviewing: "Analyzing...",
      review: "Review document",
      askQuestion: "Ask a question",
      askPlaceholder: "Ask about this document...",
      send: "Send",
      summary: "Summary",
      missing: "Missing:",
      check: "Check:",
      nextStep: "Next step:",
      disclaimer: "This review checks for completeness only and does not confirm legal validity.",
      premiumLocked: "Document review is available in the premium version.",
      premiumLockedCta: "Get your documents checked before submitting.",
      upgradeCta: "Upgrade to Premium — $99",
      documentType: {
        sin: "SIN Application",
        bank: "Bank Form",
        immigration: "Immigration Document",
        id: "ID Document",
        general: "General Form"
      }
    },
    fr: {
      title: "Révision de documents",
      subtitle: "Téléchargez vos documents et obtenez des commentaires utiles avant de soumettre",
      dragDrop: "Déposez votre fichier ici, ou cliquez pour parcourir",
      allowedTypes: "PDF, JPG, PNG (max 10 Mo)",
      uploadLocked: "Téléchargement verrouillé",
      availableWithPremium: "Disponible avec Premium",
      noDocuments: "Aucun document téléchargé",
      uploadToStart: "Téléchargez un document pour commencer",
      reviewing: "Analyse en cours...",
      review: "Réviser le document",
      askQuestion: "Poser une question",
      askPlaceholder: "Posez une question sur ce document...",
      send: "Envoyer",
      summary: "Résumé",
      missing: "Manquant:",
      check: "À vérifier:",
      nextStep: "Prochaine étape:",
      disclaimer: "Cette révision vérifie la complétude uniquement et ne confirme pas la validité juridique.",
      premiumLocked: "La révision de documents est disponible dans la version premium.",
      premiumLockedCta: "Faites vérifier vos documents avant de les soumettre.",
      upgradeCta: "Passer à Premium — 99 $",
      documentType: {
        sin: "Demande de NAS",
        bank: "Formulaire bancaire",
        immigration: "Document d'immigration",
        id: "Document d'identité",
        general: "Formulaire général"
      }
    }
  };

  const tr = translations[language] || translations.en;

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDocuments();
    }
  }, [isAuthenticated, fetchDocuments]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!isPaid) return;
    
    const file = acceptedFiles[0];
    if (!file) return;

    // Track upload started
    const fileType = file.type || file.name.split('.').pop();
    trackDocumentUploadStarted(fileType);

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchDocuments();
      trackDocumentUploadCompleted(fileType, true);
    } catch (err) {
      console.error('Upload failed:', err);
      alert(err.response?.data?.detail || 'Upload failed');
      trackDocumentUploadCompleted(fileType, false);
    } finally {
      setUploading(false);
    }
  }, [fetchDocuments, isPaid]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    disabled: !isPaid
  });

  const reviewDocument = async (documentId) => {
    if (!isPaid) return;
    setReviewing(documentId);
    try {
      const response = await axios.post(`${API}/documents/${documentId}/review?language=${language}`, {});
      fetchDocuments();
      // Show the full review result screen
      const doc = documents.find(d => d.document_id === documentId);
      if (doc) {
        setSelectedReview({
          ...doc,
          review_result: response.data
        });
      }
    } catch (err) {
      console.error('Review failed:', err);
    } finally {
      setReviewing(null);
    }
  };

  const openReviewResult = (doc) => {
    if (doc.review_result) {
      setSelectedReview(doc);
    }
  };

  const closeReviewResult = () => {
    setSelectedReview(null);
  };

  const handleRecheck = async () => {
    if (selectedReview) {
      closeReviewResult();
      await reviewDocument(selectedReview.document_id);
    }
  };

  const handleContinue = () => {
    closeReviewResult();
    navigate('/dashboard');
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer ce document ?' : 'Delete this document?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/documents/${documentId}`);
      fetchDocuments();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      
      {/* Full Screen Review Result Modal */}
      <AnimatePresence>
        {selectedReview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-pp-bg"
          >
            <div className="min-h-screen pb-12">
              {/* Header */}
              <div className="sticky top-0 bg-pp-bg border-b border-pp-border z-10">
                <div className="pp-container max-w-[720px] py-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={closeReviewResult}
                      className="flex items-center gap-2 text-pp-text-secondary hover:text-pp-text transition-colors"
                    >
                      <ArrowLeft size={20} />
                      <span className="text-sm font-medium">
                        {language === 'fr' ? 'Retour aux documents' : 'Back to documents'}
                      </span>
                    </button>
                    <button
                      onClick={closeReviewResult}
                      className="p-2 rounded-lg hover:bg-pp-secondary transition-colors"
                    >
                      <X size={20} className="text-pp-text-secondary" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Document Name */}
              <div className="pp-container max-w-[720px] pt-6 pb-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-pp-navy-light flex items-center justify-center">
                    <FileText size={20} className="text-pp-navy" />
                  </div>
                  <div>
                    <h2 className="font-medium text-pp-text">{selectedReview.filename}</h2>
                    <p className="text-sm text-pp-text-muted">
                      {selectedReview.review_result?.document_type && (
                        tr.documentType[selectedReview.review_result.document_type] || selectedReview.review_result.document_type
                      )}
                    </p>
                  </div>
                </div>

                {/* Review Result Component */}
                <DocumentReviewResult
                  reviewData={selectedReview.review_result}
                  onRecheck={handleRecheck}
                  onContinue={handleContinue}
                  isLoading={reviewing === selectedReview.document_id}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <main className="pt-24 pb-12">
        <div className="pp-container max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-2">
              <h1 className="pp-heading-2 text-pp-text" data-testid="documents-title">
                {tr.title}
              </h1>
              {isPaid && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium">
                  <Star size={14} weight="fill" />
                  Premium
                </span>
              )}
            </div>
            <p className="text-pp-text-secondary mb-8">{tr.subtitle}</p>

            {/* Free user locked state */}
            {!isPaid && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-pp-navy/5 to-pp-navy/10 border border-pp-navy/10"
                data-testid="documents-locked"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pp-navy/10 flex items-center justify-center flex-shrink-0">
                    <Lock size={24} className="text-pp-navy" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-medium text-pp-text text-lg mb-2">
                      {tr.premiumLocked}
                    </h3>
                    <p className="text-pp-text-secondary mb-5">
                      {tr.premiumLockedCta}
                    </p>
                    <Link
                      to="/pricing"
                      className="pp-btn-primary"
                      data-testid="upgrade-documents-cta"
                    >
                      {tr.upgradeCta}
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Upload Area */}
            <div
              {...getRootProps()}
              className={`pp-card border-2 border-dashed transition-all mb-8 ${
                !isPaid 
                  ? 'opacity-50 cursor-not-allowed border-pp-border'
                  : isDragActive 
                    ? 'border-pp-navy bg-pp-navy-light cursor-pointer' 
                    : 'border-pp-border hover:border-pp-navy/30 cursor-pointer'
              }`}
              data-testid="upload-dropzone"
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center py-8">
                {!isPaid ? (
                  <>
                    <Lock size={48} className="mb-4 text-pp-text-muted" />
                    <p className="font-medium text-pp-text-muted mb-1">{tr.uploadLocked}</p>
                    <p className="text-sm text-pp-text-muted">{tr.availableWithPremium}</p>
                  </>
                ) : (
                  <>
                    <CloudArrowUp 
                      size={48} 
                      className={`mb-4 ${isDragActive ? 'text-pp-navy' : 'text-pp-text-muted'}`}
                      weight="duotone"
                    />
                    <p className="font-medium text-pp-text mb-1">
                      {uploading ? tr.reviewing : tr.dragDrop}
                    </p>
                    <p className="text-sm text-pp-text-muted">{tr.allowedTypes}</p>
                  </>
                )}
              </div>
            </div>

            {/* Document List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-pp-navy border-t-transparent rounded-full animate-spin" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={48} className="text-pp-text-muted mx-auto mb-4" />
                <p className="text-pp-text-secondary">{tr.noDocuments}</p>
                {isPaid && (
                  <p className="text-sm text-pp-text-muted mt-2">{tr.uploadToStart}</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {documents.map((doc) => (
                  <DocumentCard
                    key={doc.document_id}
                    document={doc}
                    tr={tr}
                    language={language}
                    onReview={() => reviewDocument(doc.document_id)}
                    onDelete={() => deleteDocument(doc.document_id)}
                    onViewReview={() => openReviewResult(doc)}
                    reviewing={reviewing === doc.document_id}
                    isPaid={isPaid}
                  />
                ))}
              </div>
            )}

            {/* Disclaimer */}
            <div className="mt-8 p-4 rounded-xl bg-pp-warning-bg border border-pp-warning-border">
              <div className="flex gap-3">
                <Warning size={20} className="text-pp-warning-text flex-shrink-0 mt-0.5" />
                <p className="text-sm text-pp-warning-text">{tr.disclaimer}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}

function DocumentCard({ document, tr, language, onReview, onDelete, onViewReview, reviewing, isPaid }) {
  const [showAsk, setShowAsk] = useState(false);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [askResponse, setAskResponse] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA');
  };

  const handleAskQuestion = async () => {
    if (!question.trim()) return;
    setAsking(true);
    
    try {
      const response = await axios.post(
        `${API}/documents/${document.document_id}/ask?question=${encodeURIComponent(question)}&language=${language}`,
        {}
      );
      setAskResponse(response.data);
      setQuestion('');
    } catch (err) {
      console.error('Ask question failed:', err);
    } finally {
      setAsking(false);
    }
  };

  const reviewResult = document.review_result;
  const hasReview = document.review_status === 'reviewed' && reviewResult;

  // Get status badge color based on submission_status
  const getStatusBadge = () => {
    if (!hasReview) return null;
    
    const status = reviewResult.submission_status;
    const score = reviewResult.confidence_score;
    
    if (status === 'ready_to_submit' || score >= 80) {
      return {
        text: language === 'fr' ? 'Prêt' : 'Ready',
        bgColor: 'bg-[#134E4A]/10',
        textColor: 'text-[#134E4A]'
      };
    } else if (status === 'needs_fixes' || score >= 50) {
      return {
        text: language === 'fr' ? 'À corriger' : 'Needs fixes',
        bgColor: 'bg-amber-100',
        textColor: 'text-amber-700'
      };
    } else {
      return {
        text: language === 'fr' ? 'Risque élevé' : 'High risk',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700'
      };
    }
  };

  const statusBadge = getStatusBadge();

  return (
    <div className="pp-card" data-testid={`document-${document.document_id}`}>
      {/* Document Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-pp-navy-light flex items-center justify-center flex-shrink-0">
            <FileText size={24} className="text-pp-navy" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-pp-text mb-1 truncate">{document.filename}</h3>
            <div className="flex items-center gap-3 text-sm text-pp-text-muted flex-wrap">
              <span>{formatDate(document.created_at)}</span>
              {hasReview && reviewResult.document_type && (
                <>
                  <span>•</span>
                  <span className="px-2 py-0.5 rounded bg-pp-secondary text-pp-text-muted text-xs">
                    {tr.documentType[reviewResult.document_type] || reviewResult.document_type}
                  </span>
                </>
              )}
              {statusBadge && (
                <>
                  <span>•</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge.bgColor} ${statusBadge.textColor}`}>
                    {statusBadge.text}
                    {reviewResult.confidence_score && ` (${reviewResult.confidence_score}%)`}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {document.review_status === 'pending' && isPaid && (
            <button
              onClick={onReview}
              disabled={reviewing}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-pp-navy text-white text-sm font-medium hover:bg-pp-navy-hover transition-colors disabled:opacity-50"
              data-testid={`review-${document.document_id}`}
            >
              {reviewing ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  {tr.reviewing}
                </>
              ) : (
                <>
                  <MagnifyingGlass size={16} />
                  {tr.review}
                </>
              )}
            </button>
          )}
          {hasReview && (
            <button
              onClick={onViewReview}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-pp-secondary text-pp-navy text-sm font-medium hover:bg-pp-navy-light transition-colors"
              data-testid={`view-review-${document.document_id}`}
            >
              <CheckCircle size={16} />
              {language === 'fr' ? 'Voir résultat' : 'View result'}
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-2 rounded-xl text-pp-text-muted hover:text-pp-error-text hover:bg-pp-error-bg transition-colors"
            data-testid={`delete-${document.document_id}`}
          >
            <Trash size={18} />
          </button>
        </div>
      </div>

      {/* Quick Review Summary (collapsed view) */}
      {hasReview && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 p-4 rounded-xl bg-[#F8F9FB] border border-pp-border cursor-pointer hover:bg-pp-secondary/50 transition-colors"
          onClick={onViewReview}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-pp-text-secondary line-clamp-2">
                {reviewResult.summary}
              </p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <span className="text-sm text-pp-navy font-medium">
                {language === 'fr' ? 'Détails →' : 'Details →'}
              </span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Pending Review Status */}
      {document.review_status === 'pending' && !isPaid && (
        <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-pp-secondary/50">
          <Lock size={16} className="text-pp-text-muted" />
          <span className="text-sm text-pp-text-muted">{tr.premiumLocked}</span>
        </div>
      )}
    </div>
  );
}

